//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Input } from '../ui/input';
import { useTranslation } from 'react-i18next';
import { isSafeKey, safeAssignKey } from '../../utils/securityUtils';

interface SelectedOptionsState {
  all: boolean;
  [key: string]: boolean;
}

export interface FilterState {
  teamspace: string[];
  loops: string[];
  licenses: string[];
  role: string[];
}

interface FilterDropdownProps {
  label: string;
  options: string[];
  filterType: keyof FilterState;
  onFilterChange: (
    filterType: keyof FilterState,
    selectedValues: string[],
  ) => void;
  selectedValues: string[];
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  filterType,
  onFilterChange,
  selectedValues,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsState>({
    all: true,
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const validOptions = options.filter(
      (option) =>
        option && option !== t('loading-trans') && option !== t('no-data'),
    );

    const isAllSelected = selectedValues.length === 0;

    const newState: SelectedOptionsState = { all: isAllSelected };
    validOptions.forEach((option) => {
      safeAssignKey(
        newState,
        option,
        isAllSelected || selectedValues.includes(option),
      );
    });

    const isDifferent =
      JSON.stringify(selectedOptions) !== JSON.stringify(newState);
    if (isDifferent) {
      queueMicrotask(() => setSelectedOptions(newState));
    }
  }, [options, selectedValues, t]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    // Prevent prototype pollution via crafted checkbox names
    if (!isSafeKey(name)) return;

    setSelectedOptions((prevState) => {
      const newState = { ...prevState };
      const validOptions = options.filter(
        (option) =>
          option && option !== t('loading-trans') && option !== t('no-data'),
      );

      if (name === 'all') {
        newState.all = checked;
        validOptions.forEach((option) => {
          safeAssignKey(newState, option, checked);
        });

        if (checked) {
          onFilterChange(filterType, []);
        } else {
          onFilterChange(filterType, ['__NONE_SELECTED__']);
        }

        return newState;
      } else {
        safeAssignKey(newState, name, checked);

        const selectedVals = validOptions.filter((option) => newState[option]);
        const allSelected =
          selectedVals.length === validOptions.length &&
          validOptions.length > 0;

        newState.all = allSelected;
        onFilterChange(filterType, allSelected ? [] : selectedVals);

        return newState;
      }
    });
  };

  const filteredOptions = options.filter(
    (option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase()) &&
      option !== t('loading-trans'),
  );

  const hasNoDataSelected = options.some(
    (opt) => opt === t('no-data') && selectedOptions[opt],
  );

  const selectedOptionsList = options.filter(
    (opt) =>
      opt !== t('loading-trans') &&
      selectedOptions[opt] &&
      opt !== t('no-data'),
  );

  const displayText = selectedOptions.all
    ? t('all')
    : hasNoDataSelected
      ? t('no-data')
      : selectedOptionsList.length > 0
        ? selectedOptionsList.join(', ')
        : 'Select';

  const tooltipText =
    selectedOptions.all || hasNoDataSelected
      ? ''
      : selectedOptionsList.length > 0
        ? selectedOptionsList.join(', ')
        : '';

  return (
    <div
      className="flex items-center gap-1"
      id="id-filter-dropdown-container"
      data-testid="filter-dropdown-container"
    >
      <span
        className="text-xs text-gray-500 dark:text-gray-400"
        id="id-filter-dropdown-label"
        data-testid="filter-dropdown-label"
      >
        {label}:
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={open}
                  aria-label={`User administration filter: ${label}${displayText !== 'Select' ? ', ' + displayText : ''}`}
                  id={`id-${label.toLowerCase().replace(/\s+/g, '-')}-filter`}
                  data-testid={`${label.toLowerCase().replace(/\s+/g, '-')}-filter`}
                  className={`flex items-center gap-1 rounded border px-2 py-1 text-xs transition-colors ${
                    selectedOptions.all
                      ? 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-carbon-darkPanel dark:text-gray-300'
                      : 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/20 dark:text-blue-300'
                  }`}
                >
                  <span
                    className="max-w-[120px] truncate"
                    id="id-filter-dropdown-display-text"
                    data-testid="filter-dropdown-display-text"
                  >
                    {displayText}
                  </span>
                  <ChevronDown size={14} />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            {tooltipText && !selectedOptions.all && (
              <TooltipContent>{tooltipText}</TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <PopoverContent
          align="start"
          className="max-h-[400px] w-[250px] overflow-auto p-3"
          id={`id-${label.toLowerCase().replace(/\s+/g, '-')}-popover`}
          data-testid={`${label.toLowerCase().replace(/\s+/g, '-')}-popover`}
        >
          <div
            className="relative mb-2"
            id="id-filter-dropdown-search-wrapper"
            data-testid="filter-dropdown-search-wrapper"
          >
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              placeholder={t('search-placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>

          <div
            className="space-y-1"
            id="id-filter-dropdown-options-list"
            data-testid="filter-dropdown-options-list"
          >
            <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">
              <input
                type="checkbox"
                checked={selectedOptions.all}
                onChange={handleCheckboxChange}
                name="all"
                id="id-checkbox-all"
                data-testid="checkbox-all"
                className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t('all')}
            </label>
            {filteredOptions
              .filter((option) => option !== t('loading-trans'))
              .map((option) => (
                <label
                  key={option}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={!!selectedOptions[option]}
                    onChange={handleCheckboxChange}
                    name={option}
                    id={`id-checkbox-${option.toLowerCase().replace(/\s+/g, '-')}`}
                    data-testid={`checkbox-${option.toLowerCase().replace(/\s+/g, '-')}`}
                    className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span
                    className="truncate text-sm text-gray-700 dark:text-gray-300"
                    id="id-filter-dropdown-option-text"
                    data-testid="filter-dropdown-option-text"
                  >
                    {option}
                  </span>
                </label>
              ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

interface FilterBarUser {
  teamspaces?: string[];
  loops?: string[];
}

const FilterBar: React.FC<{
  onFiltersChange: (filters: FilterState) => void;
  filters: FilterState;
  prefetchedTeamspaceNames?: string[] | null;
  prefetchedLoopNames?: string[] | null;
  /** Users with their teamspace and loop assignments (kept for fallback). */
  users?: FilterBarUser[];
  /** Direct loop-name → teamspace-name mapping from the Loop Server's
   *  teamSpaceId field. Preferred over user-membership derivation. */
  loopToTeamspaceNames?: Record<string, string>;
}> = ({
  onFiltersChange,
  filters,
  prefetchedTeamspaceNames,
  prefetchedLoopNames,
  users,
  loopToTeamspaceNames,
}) => {
  const { t } = useTranslation();

  // Use pre-fetched data from parent (UsersPanel) — no API calls needed here.
  const [teamspaceNames, setTeamspaceNames] = useState<string[]>(
    prefetchedTeamspaceNames ?? [],
  );
  const [allLoopNames, setAllLoopNames] = useState<string[]>(
    prefetchedLoopNames ?? [],
  );

  // Sync when parent updates prefetched data
  useEffect(() => {
    if (prefetchedTeamspaceNames != null) {
      queueMicrotask(() => {
        setTeamspaceNames(prefetchedTeamspaceNames);
      });
    }
  }, [prefetchedTeamspaceNames]);

  useEffect(() => {
    if (prefetchedLoopNames != null) {
      queueMicrotask(() => {
        setAllLoopNames(prefetchedLoopNames);
      });
    }
  }, [prefetchedLoopNames]);

  // Sort teamspace names alphabetically (case-insensitive)
  const sortedTeamspaceNames = useMemo(
    () =>
      [...teamspaceNames].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [teamspaceNames],
  );

  // Derive visible loop names: when a teamspace filter is active, only show
  // loops whose owning teamspace matches the selection. Uses the direct
  // loopToTeamspaceNames mapping (loop → teamspace name) when available;
  // falls back to user-membership derivation otherwise.
  const visibleLoopNames = useMemo(() => {
    const hasTeamspaceFilter =
      filters.teamspace.length > 0 &&
      !filters.teamspace.includes('__NONE_SELECTED__');

    if (!hasTeamspaceFilter) {
      return [...allLoopNames].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      );
    }

    const selectedTs = new Set(filters.teamspace);
    let loops: string[];

    // Prefer direct mapping (loop → owning teamspace name).
    if (loopToTeamspaceNames && Object.keys(loopToTeamspaceNames).length > 0) {
      loops = allLoopNames.filter((lp) => {
        const owningTs = loopToTeamspaceNames[lp];
        return owningTs != null && selectedTs.has(owningTs);
      });
    } else if (users) {
      // Fallback: derive from user-teamspace membership.
      const loopSet = new Set<string>();
      for (const u of users) {
        if (u.teamspaces?.some((ts) => selectedTs.has(ts))) {
          u.loops?.forEach((lp) => {
            if (lp) loopSet.add(lp);
          });
        }
      }
      loops = Array.from(loopSet);
    } else {
      loops = allLoopNames;
    }

    return [...loops].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }, [allLoopNames, filters.teamspace, loopToTeamspaceNames, users]);

  const handleFilterChange = (
    filterType: keyof FilterState,
    selectedValues: string[],
  ) => {
    const newFilters = {
      ...filters,
      [filterType]: selectedValues,
    };
    // When the teamspace filter changes, reset the loops filter so stale
    // loop selections from a previous teamspace scope don't hide users.
    if (filterType === 'teamspace') {
      newFilters.loops = [];
    }
    onFiltersChange(newFilters);
  };

  return (
    <div
      className="flex items-center gap-3"
      id="id-filter-bar"
      data-testid="filter-bar"
    >
      <FilterDropdown
        label={t('team-space')}
        options={sortedTeamspaceNames}
        filterType="teamspace"
        onFilterChange={handleFilterChange}
        selectedValues={filters.teamspace}
      />
      <div
        className="h-5 w-px bg-gray-300 dark:bg-gray-600"
        id="id-filter-dropdown-separator"
        data-testid="filter-dropdown-separator"
      />
      <FilterDropdown
        label={t('loops')}
        options={visibleLoopNames}
        filterType="loops"
        onFilterChange={handleFilterChange}
        selectedValues={filters.loops}
      />
    </div>
  );
};

export default FilterBar;
